import { useCallback, useState } from 'react';
import { Button } from '@Components/Button';
import { Dropdown } from '@Components/Dropdown';
import { AlertDialog } from '@Components/AlertDialog';
import { Intent, Placement, Size } from '@Components/designConstants';
import { CpsMonthReportStats, MONTHS, getYearOptions, getPreviousMonthIndex } from './cps-utils';
import { ClientToolsLambdaFetch, fetchCometSelfInvoke } from '../../utils/fetch-utils';
import { useSetError } from '../../redux/modals';

interface FinanceReportSectionProps {
  stats: CpsMonthReportStats | null;
}

const FinanceReportSection = ({ stats }: FinanceReportSectionProps) => {
  const setError = useSetError();
  const [month, setMonth] = useState(MONTHS[getPreviousMonthIndex()]);
  const [year, setYear] = useState(String(new Date().getFullYear()));
  const [loading, setLoading] = useState(false);
  const [successUrl, setSuccessUrl] = useState('');

  const yearOptions = getYearOptions();

  const handleDownload = useCallback(async () => {
    setLoading(true);
    try {
      const monthIndex = MONTHS.indexOf(month) + 1;
      const dateStr = `${year}-${String(monthIndex).padStart(2, '0')}-01`;
      const result = await fetchCometSelfInvoke<{ date: string }, { url: string }>(
        ClientToolsLambdaFetch,
        '/cps/buildFinanceReport',
        { date: dateStr },
        { noTimeout: true }
      );
      setSuccessUrl(result.url);
      window.open(result.url, '_blank');
    } catch (e) {
      setError({ message: `Failed to generate finance report: ${e.message}`, reportError: e });
    } finally {
      setLoading(false);
    }
  }, [month, year, setError]);

  return (
    <div className='cps-admin-section'>
      <h6>Finance Report</h6>

      {stats && (
        <div className='cps-admin-stats'>
          <div className='cps-admin-stat'>
            <span className='cps-admin-stat-value'>{stats.expectedSurveys}</span>
            <span className='cps-admin-stat-label'>Expected</span>
          </div>
          <div className='cps-admin-stat'>
            <span className='cps-admin-stat-value'>{stats.surveysSubmitted}</span>
            <span className='cps-admin-stat-label'>Submitted</span>
          </div>
          <div className='cps-admin-stat'>
            <span className='cps-admin-stat-value'>{stats.surveysApproved}</span>
            <span className='cps-admin-stat-label'>Approved</span>
          </div>
        </div>
      )}

      <div className='cps-admin-finance-controls'>
        <Dropdown
          overlayPlacement={Placement.RIGHT}
          size={Size.LG}
          options={[...MONTHS]}
          value={month}
          onChange={setMonth}
          label='Month'
        />
        <Dropdown
          overlayPlacement={Placement.RIGHT}
          size={Size.LG}
          options={yearOptions}
          value={year}
          onChange={setYear}
          label='Year'
        />
        <Button size={Size.LG} onClick={() => handleDownload()} loading={loading}>
          Download CSV
        </Button>
      </div>

      <AlertDialog
        show={!!successUrl}
        onHide={() => setSuccessUrl('')}
        intent={Intent.SUCCESS}
        title='Report Ready'
        body="Finance report generated. If the download didn't start automatically, click the link below."
        primaryAction={{ onClick: () => setSuccessUrl('') }}
      />
    </div>
  );
};

export { FinanceReportSection };
